import { BaseService } from '../base/BaseService';
import type { QueryOptions, ServiceResponse } from '../base/ServiceTypes';

import { env } from '@/config/environment';
import type { Json } from '@/integrations/supabase/types';
import type { PlatformConnectionState } from '@/services/socialAuth/connection-state';
import { resolveConnectionState } from '@/services/socialAuth/connection-state';
import type { ShopifyProductDB } from '@/types/product';
import { logger } from '@/utils/logger';

// Use centralized product interface to prevent inconsistencies
export type ShopifyProduct = ShopifyProductDB;

export interface ShopifyConnection {
  id: string;
  shop_domain: string;
  name?: string;
  connected_at: string;
  status: 'active' | 'inactive' | 'error';
  scopes?: string;
  /**
   * The resolved state behind `status`. `never_connected` never appears here —
   * it is represented by a `null` connection. See
   * `@/services/socialAuth/connection-state` for why `disconnected` and
   * `never_connected` must stay distinguishable.
   */
  state: PlatformConnectionState;
  /** Superseded reconnect rows for the same platform. Informational. */
  supersededCount: number;
}

export interface SyncResult {
  success: boolean;
  productsUpdated: number;
  collectionsUpdated?: number;
  message?: string;
  error?: string;
}

export interface BoardProductScope {
  ownerId: string;
  brandKitId: string;
  /** Part of the cache identity so a context change can never replay old rows. */
  revision: number;
}

/**
 * Scope shared by `getProducts` and `getStoreConnection`. Optional so the
 * unscoped call shape (no active workspace resolved yet) still compiles, but
 * every production caller now resolves and passes it — `shopify_products` has
 * no `brand_kit_id` column and `user_connections` rows are readable across
 * every workspace the account owns (`private.my_workspace_ids()`), so without
 * this a workspace with no Shopify connection of its own inherits whichever
 * other workspace on the account happens to be connected.
 */
export interface ProductWorkspaceScope {
  ownerId: string;
  brandKitId: string;
  /** Exact connection selected by the workspace, when one is available. */
  connectionId?: string;
}

function normalizeBoardProduct(row: Record<string, unknown>): ShopifyProduct {
  return {
    ...(row as unknown as ShopifyProduct),
    shopify_product_id: String(row.shopify_product_id),
    // The canonical database column is `myshopify_domain`; the shared UI type
    // still exposes its historical `shop_domain` name.
    shop_domain: String(row.myshopify_domain ?? ''),
  };
}

export class ProductService extends BaseService {
  private static instance: ProductService;

  private constructor() {
    super();
  }

  static getInstance(): ProductService {
    if (!ProductService.instance) {
      ProductService.instance = new ProductService();
    }
    return ProductService.instance;
  }

  /**
   * The domains bound to a workspace via `brand_kit_store_bindings` — the
   * only table that ties a `myshopify_domain` to a specific `brand_kit_id`.
   * Shared by every `shopify_products` reader that must not fall back to the
   * owner's whole cross-workspace catalog.
   */
  private async resolveBoundDomains(scope: ProductWorkspaceScope): Promise<string[] | null> {
    const ownerId = scope.ownerId.trim();
    const brandKitId = scope.brandKitId.trim();
    if (!ownerId || !brandKitId) return null;

    const { data: bindingRows, error: bindingError } = await this.client
      .from('brand_kit_store_bindings')
      .select('myshopify_domain')
      .eq('owner_id', ownerId)
      .eq('brand_kit_id', brandKitId);

    const recoverableCodes = new Set(['42P01', '42703', 'PGRST116']);
    if (bindingError && !recoverableCodes.has(bindingError.code ?? '')) {
      throw bindingError;
    }
    if (bindingError) return null;

    return [
      ...new Set(
        ((bindingRows ?? []) as Array<{ myshopify_domain?: unknown }>)
          .map((row) =>
            typeof row.myshopify_domain === 'string'
              ? row.myshopify_domain.trim().toLowerCase()
              : '',
          )
          .filter(Boolean),
      ),
    ];
  }

  /**
   * Sync products from Shopify using the consolidated function
   */
  async syncProducts(
    scope?: ProductWorkspaceScope,
    _options?: QueryOptions,
  ): Promise<ServiceResponse<SyncResult>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      try {
        const { data, error } = await this.client.functions.invoke<SyncResult>(
          'sync-shopify-products',
          {
            body: {
              userId,
              ...(scope?.brandKitId ? { brandKitId: scope.brandKitId } : {}),
              ...(scope?.connectionId ? { connectionId: scope.connectionId } : {}),
            },
          },
        );

        if (error) {
          logger.error('INVOKE ERROR:', error);
          logger.error('INVOKE ERROR CONTEXT:', JSON.stringify(error.context));
          let detail = error.message;
          const response = (error as { context?: Response }).context;
          if (response && typeof response.clone === 'function') {
            const payload = await response
              .clone()
              .json()
              .catch(() => null as { error?: unknown } | null);
            if (typeof payload?.error === 'string' && payload.error.length > 0) {
              detail = payload.error;
            }
          }
          throw new Error(`Sync failed: ${detail}`);
        }

        if (!data) throw new Error('No sync result returned');

        // Clear every scoped Shopify read after a successful sync. The cache
        // key includes owner + workspace, so removing only the legacy
        // user-level key leaves the product page showing stale prices/counts.
        this.clearShopifyCaches();

        return data;
      } catch (error) {
        this.log.error('Product sync failed:', error);
        if (error instanceof Error) {
          throw error;
        }
        throw new Error('An unexpected error occurred while syncing products');
      }
    });
  }

  /**
   * Drop every cached Shopify read (`shopify-connection-*`, `shopify-products-*`).
   * Called right after a store connects/disconnects — without this, the
   * 10-minute executeWithCache TTL kept the wizard's "Pick from Shopify" tile
   * and the agent's useProducts default stale long after a successful OAuth
   * (UAT 2026-06-12, F-081).
   */
  clearShopifyCaches(): void {
    this.invalidateCacheByPattern('shopify-');
  }

  /**
   * Push a generated image into a Shopify product's media gallery
   * (productCreateMedia). Requires an already-connected store — the
   * write_products scope is granted at OAuth-connect time, so this never
   * triggers a re-auth prompt.
   */
  async pushImageToShopify(
    productId: string,
    imageUrl: string,
    alt?: string,
  ): Promise<ServiceResponse<{ success: boolean }>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();

      const { data, error } = await this.client.functions.invoke<{
        success?: boolean;
        error?: string;
      }>('push-image-to-shopify', {
        body: { productId, imageUrl, alt },
      });

      if (error) {
        logger.error('INVOKE ERROR:', error);
        logger.error('INVOKE ERROR CONTEXT:', JSON.stringify(error.context));
        throw new Error(`Push to Shopify failed: ${error.message}`);
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to push image to Shopify');
      }

      return { success: true };
    });
  }

  /**
   * Get all Shopify products for the authenticated user, scoped to the
   * active workspace. `shopify_products` carries no `brand_kit_id` of its
   * own, so `scope` resolves the workspace's bound store domain(s) through
   * `brand_kit_store_bindings` first — the same mechanism `getBoardProducts`
   * uses. Without `scope` this reads every store the account has ever
   * synced, which is only safe for the legacy unscoped caller (a dev-only
   * harness, never routed).
   */
  async getProducts(
    scope?: ProductWorkspaceScope,
    options?: QueryOptions,
  ): Promise<ServiceResponse<ShopifyProduct[]>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const cacheKey = scope
        ? `shopify-products-${userId}-${scope.ownerId}-${scope.brandKitId}`
        : `shopify-products-${userId}`;

      return this.executeWithCache(
        cacheKey,
        async () => {
          const recoverableCodes = new Set(['42P01', '42703', 'PGRST116']);
          let domains: string[] | null = null;
          if (scope) {
            domains = await this.resolveBoundDomains(scope);
            if (!domains || domains.length === 0) return [];
          }

          let query = this.client.from('shopify_products').select('*').eq('status', 'active');
          if (domains) {
            query = query.in('myshopify_domain', domains);
          }
          const { data, error } = await query.order('created_at', { ascending: false });

          if (error && !recoverableCodes.has(error.code ?? '')) {
            throw error;
          }

          // The live Shopify fallback below hits whichever store the account's
          // connection token belongs to — never reached for a scoped, bound
          // workspace with zero local rows, since a real bound domain with no
          // synced products should surface as empty, not silently borrow
          // another workspace's live catalog.
          if (!scope && (error || !data || data.length === 0)) {
            try {
              const session = await this.client.auth.getSession();
              const url = `${env.supabase.url}/functions/v1/search-shopify-live?q=*`;
              const res = await fetch(url, {
                method: 'GET',
                headers: {
                  Authorization: `Bearer ${session.data.session?.access_token ?? ''}`,
                },
              });
              const bodyText = await res.text();
              let json: { products?: unknown[]; error?: string } = {};
              try {
                json = bodyText ? JSON.parse(bodyText) : {};
              } catch {
                throw new Error(
                  `Shopify returned ${res.status} ${res.statusText || 'non-JSON response'}`,
                );
              }
              if (!res.ok) {
                throw new Error(json.error || `Shopify returned ${res.status}`);
              }
              const liveProducts = Array.isArray(json.products) ? json.products : [];
              return liveProducts.map(
                (p: {
                  id: string;
                  shopifyProductId: number;
                  title: string;
                  handle: string | null;
                  status: string;
                  featuredImage: unknown;
                  price: string | null;
                }) => ({
                  id: p.id,
                  shopify_product_id: String(p.shopifyProductId),
                  user_id: userId,
                  shop_domain: '',
                  title: p.title,
                  description: null,
                  handle: p.handle,
                  vendor: null,
                  product_type: null,
                  tags: [],
                  status: (p.status?.toLowerCase() ?? 'draft') as 'active' | 'archived' | 'draft',
                  published_at:
                    p.status?.toUpperCase() === 'ACTIVE' ? new Date().toISOString() : null,
                  variants: [],
                  images: p.featuredImage
                    ? [p.featuredImage as { url: string; altText?: string }]
                    : [],
                  options: [],
                  min_price: p.price ? parseFloat(p.price) : 0,
                  max_price: p.price ? parseFloat(p.price) : 0,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                }),
              ) as unknown as ShopifyProduct[];
            } catch (liveErr) {
              this.log.warn('Fallback live fetch failed:', liveErr);
              throw liveErr instanceof Error
                ? liveErr
                : new Error("Couldn't load Shopify products live.");
            }
          }

          return (data ?? []).map((row) =>
            normalizeBoardProduct(row as unknown as Record<string, unknown>),
          );
        },
        { ttl: 5 * 60 * 1000, ...options },
      );
    });
  }

  /**
   * Small active-product slice for idea surfaces. Keeps pages like Content Board
   * from loading search/sync state or a full catalog when they only need seeds.
   */
  async getBoardProducts(
    scope: BoardProductScope,
    limit = 6,
  ): Promise<ServiceResponse<ShopifyProduct[]>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const ownerId = scope.ownerId.trim();
      const brandKitId = scope.brandKitId.trim();
      if (!ownerId || !brandKitId) return [];
      const safeLimit = Math.max(1, Math.min(24, Math.floor(limit)));
      const cacheKey = `shopify-board-products-${ownerId}-${brandKitId}-${scope.revision}-${safeLimit}`;

      return this.executeWithCache(
        cacheKey,
        async () => {
          // Products inherit kit scope through the store binding. Never copy a
          // product row onto a kit, and never fall back to the owner's whole
          // catalog when a binding is absent or still awaiting remediation.
          const { data: bindingRows, error: bindingError } = await this.client
            .from('brand_kit_store_bindings')
            .select('myshopify_domain')
            .eq('owner_id', ownerId)
            .eq('brand_kit_id', brandKitId);

          const recoverableCodes = new Set(['42P01', '42703', 'PGRST116']);
          if (bindingError && !recoverableCodes.has(bindingError.code ?? '')) {
            throw bindingError;
          }
          if (bindingError) return [];

          const domains = [
            ...new Set(
              ((bindingRows ?? []) as Array<{ myshopify_domain?: unknown }>)
                .map((row) =>
                  typeof row.myshopify_domain === 'string'
                    ? row.myshopify_domain.trim().toLowerCase()
                    : '',
                )
                .filter(Boolean),
            ),
          ];
          if (domains.length === 0) return [];

          const { data, error } = await this.client
            .from('shopify_products')
            .select('*')
            .eq('user_id', ownerId)
            .in('myshopify_domain', domains)
            .eq('status', 'active')
            .order('created_at', { ascending: false })
            .limit(safeLimit);

          if (error && !recoverableCodes.has(error.code ?? '')) {
            throw error;
          }

          return error
            ? []
            : ((data ?? []) as unknown as Array<Record<string, unknown>>).map(
                normalizeBoardProduct,
              );
        },
        { ttl: 10 * 60 * 1000 },
      );
    });
  }

  /**
   * JIT live search: query Shopify API directly (for products not in Smart Cache).
   */
  async searchShopifyLive(
    query: string,
    scope?: ProductWorkspaceScope,
    _options?: QueryOptions,
  ): Promise<
    ServiceResponse<
      {
        id: string;
        shopifyProductId: number;
        title: string;
        handle: string | null;
        status: string;
        featuredImage: unknown;
        price: string | null;
      }[]
    >
  > {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      // Invoke with GET and query params: use URL
      const url = new URL(`${env.supabase.url}/functions/v1/search-shopify-live`);
      url.searchParams.set('q', query);
      if (scope?.brandKitId) url.searchParams.set('brand_kit_id', scope.brandKitId);
      if (scope?.connectionId) url.searchParams.set('connection_id', scope.connectionId);
      const session = await this.client.auth.getSession();
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${session.data.session?.access_token ?? ''}`,
        },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error ?? 'Live search failed');
      }
      const json = (await res.json()) as {
        products: {
          id: string;
          shopifyProductId: number;
          title: string;
          handle: string | null;
          status: string;
          featuredImage: unknown;
          price: string | null;
        }[];
      };
      return json.products ?? [];
    }, undefined);
  }

  /**
   * JIT live search: query Shopify API directly (for collections).
   */
  async searchShopifyCollections(
    query: string,
    scope?: ProductWorkspaceScope,
    _options?: QueryOptions,
  ): Promise<
    ServiceResponse<
      {
        id: string;
        shopifyCollectionId: number;
        title: string;
        handle: string | null;
        image: unknown;
      }[]
    >
  > {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      // Invoke with GET and query params: use URL
      const url = new URL(`${env.supabase.url}/functions/v1/search-shopify-collections`);
      url.searchParams.set('q', query);
      if (scope?.brandKitId) url.searchParams.set('brand_kit_id', scope.brandKitId);
      if (scope?.connectionId) url.searchParams.set('connection_id', scope.connectionId);
      const session = await this.client.auth.getSession();
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${session.data.session?.access_token ?? ''}`,
        },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error ?? 'Collection live search failed');
      }
      const json = (await res.json()) as {
        collections: {
          id: string;
          shopifyCollectionId: number;
          title: string;
          handle: string | null;
          image: unknown;
        }[];
      };
      return json.collections ?? [];
    }, undefined);
  }

  /**
   * Save a product from live search to the Smart Cache (JIT upsert).
   */
  async saveProductToCache(
    shopifyProductIdOrGid: string | number,
    scope?: ProductWorkspaceScope,
    _options?: QueryOptions,
  ): Promise<ServiceResponse<{ success: boolean; product?: unknown }>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const id =
        typeof shopifyProductIdOrGid === 'number'
          ? `gid://shopify/Product/${shopifyProductIdOrGid}`
          : String(shopifyProductIdOrGid).startsWith('gid://')
            ? shopifyProductIdOrGid
            : `gid://shopify/Product/${shopifyProductIdOrGid}`;
      const session = await this.client.auth.getSession();
      const res = await fetch(`${env.supabase.url}/functions/v1/search-shopify-live`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.data.session?.access_token ?? ''}`,
        },
        body: JSON.stringify({
          id,
          ...(scope?.brandKitId ? { brand_kit_id: scope.brandKitId } : {}),
          ...(scope?.connectionId ? { connection_id: scope.connectionId } : {}),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error ?? 'Failed to save product');
      }
      return (await res.json()) as { success: boolean; product?: unknown };
    });
  }

  /**
   * Search products by title (Live JIT search via Shopify API)
   */
  async searchProducts(
    query: string,
    scope?: ProductWorkspaceScope,
    options?: QueryOptions,
  ): Promise<ServiceResponse<ShopifyProduct[]>> {
    if (!query.trim()) {
      return this.getProducts(scope, options);
    }

    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      // Perform live search instead of just querying local DB
      const res = await this.searchShopifyLive(query, scope, options);
      if (res.error) throw res.error;
      const liveProducts = res.data ?? [];

      // Map live results to our ShopifyProduct interface
      return liveProducts.map((p) => ({
        id: p.id, // Using GID here temporarily; when saved, it will get a real UUID
        shopify_product_id: String(p.shopifyProductId),
        user_id: userId,
        shop_domain: '', // Handled by backend when saving
        title: p.title,
        description: null,
        handle: p.handle,
        vendor: null,
        product_type: null,
        tags: [],
        status: p.status.toLowerCase() as 'active' | 'archived' | 'draft',
        published_at: p.status === 'ACTIVE' ? new Date().toISOString() : null,
        variants: [],
        images: p.featuredImage ? [p.featuredImage as { url: string; altText?: string }] : [],
        options: [],
        min_price: p.price ? parseFloat(p.price) : 0,
        max_price: p.price ? parseFloat(p.price) : 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }));
    });
  }

  /**
   * Get product by ID (supports querying by both database UUID 'id' and Shopify string 'shopify_product_id')
   */
  async getProductById(
    productId: string,
    scope?: ProductWorkspaceScope,
    options?: QueryOptions,
  ): Promise<ServiceResponse<ShopifyProduct | null>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const cacheKey = scope
        ? `shopify-product-${userId}-${productId}-${scope.ownerId}-${scope.brandKitId}`
        : `shopify-product-${userId}-${productId}`;

      return this.executeWithCache(
        cacheKey,
        async () => {
          const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
            productId,
          );
          let domains: string[] | null = null;
          if (scope) {
            domains = await this.resolveBoundDomains(scope);
            if (!domains || domains.length === 0) return null;
          }

          const query = this.client.from('shopify_products').select('*');

          if (isUuid) {
            query.eq('id', productId);
          } else {
            query.eq('shopify_product_id', Number(productId));
          }
          if (domains) query.in('myshopify_domain', domains);

          const { data, error } = await query.maybeSingle();

          if (error) {
            if (error.code === '42P01') {
              this.log.warn('shopify_products table does not exist - returning null');
              return null;
            }
            throw error;
          }
          return data ? normalizeBoardProduct(data as unknown as Record<string, unknown>) : null;
        },
        { ttl: 10 * 60 * 1000, ...options },
      );
    });
  }

  /**
   * Get the Shopify store connection for the active workspace. `scope` is
   * optional only for the legacy unscoped caller (a dev-only harness, never
   * routed) — every production caller resolves and passes it. Without it,
   * `user_connections.brand_kit_id` is ignored and RLS lets the query see
   * every workspace the account owns (`private.my_workspace_ids()` returns
   * ALL of an owner's kits, not just the active one), so a workspace with no
   * Shopify connection of its own silently inherits another workspace's.
   */
  async getStoreConnection(
    scope?: ProductWorkspaceScope,
    options?: QueryOptions,
  ): Promise<ServiceResponse<ShopifyConnection | null>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const cacheKey = scope
        ? `shopify-connection-${userId}-${scope.ownerId}-${scope.brandKitId}`
        : `shopify-connection-${userId}`;

      return this.executeWithCache(
        cacheKey,
        async () => {
          // NOT `.eq('is_active', true).limit(1)`. That filter is what made a
          // lapsed store indistinguishable from a store that was never
          // connected: it returned null, the picker rendered "Connect Shopify",
          // and the merchant's already-synced products stayed invisible. It
          // also made the `status` ternary below dead code — the filter
          // guaranteed the branch it was written to express could never run.
          // `(user_id, platform)` is not unique either (a reconnect inserts a
          // new row and leaves the old one), so this reads every row and lets
          // resolveConnectionState decide over the whole set.
          let query = this.client
            .from('user_connections')
            .select(
              'id, platform_user_id, metadata, connection_data, is_active, connected_at, updated_at',
            )
            .eq('platform', 'shopify');
          if (scope) {
            query = query.eq('user_id', scope.ownerId).eq('brand_kit_id', scope.brandKitId);
          }
          const { data, error } = await query.order('updated_at', { ascending: false });

          if (error) {
            if (error.code === '42P01') {
              this.log.warn('user_connections table does not exist - returning null');
              return null;
            }
            throw error;
          }

          const resolved = resolveConnectionState(data ?? []);
          if (!resolved.row) return null;

          const row = resolved.row;
          type JsonRecord = Record<string, Json | undefined>;
          const metadata = (row.metadata as JsonRecord | null) ?? {};
          const connectionData = (row.connection_data as JsonRecord | null) ?? {};

          return {
            id: row.id,
            shop_domain: row.platform_user_id,
            name: metadata.shop_name || connectionData.shop_name || row.platform_user_id,
            connected_at: row.connected_at,
            status: resolved.state === 'active' ? 'active' : 'inactive',
            state: resolved.state,
            supersededCount: resolved.supersededCount,
            scopes: metadata.scopes || connectionData.scopes || '',
          } as ShopifyConnection;
        },
        { ttl: 10 * 60 * 1000, ...options },
      );
    });
  }
}

export const productService = ProductService.getInstance();
